import { SkiPlan } from "../types";
import { pb, pbPublic, PlanRecord, ImageRecord, UserRecord, buildFileUrl, isAuthenticated } from "./pocketbaseClient";

type ApiResponse<T> = { data?: T; error?: string };

function pbFilterDate(d: Date) {
    return d.toISOString().slice(0, 19).replace("T", " ");
}

function assertAuth(): ApiResponse<never> | null {
    if (!isAuthenticated()) return { error: "Non authentifié" };
    return null;
}

function safeJsonParse<T>(value: any, fallback: T): T {
    try {
        if (typeof value === "string") return JSON.parse(value) as T;
        if (value && typeof value === "object") return value as T;
        return fallback;
    } catch {
        return fallback;
    }
}

function canEditRecord(rec: PlanRecord, userId: string) {
    const isOwner = rec.userId === userId;
    const isCollab = (rec.collaborators ?? []).includes(userId);
    return isOwner || isCollab;
}

export class PocketBaseService {
    // ===== AUTH =====
    async signup(email: string, password: string, name: string): Promise<ApiResponse<any>> {
        try {
            const user = await pb.collection("users").create({
                email,
                password,
                passwordConfirm: password,
                name,
                emailVisibility: true,
            });
            await pb.collection("users").authWithPassword(email, password);
            return { data: user };
        } catch (error: any) {
            console.error("Signup error:", error?.status, error?.data, error?.message);
            return { error: error?.message || "Erreur lors de l'inscription" };
        }
    }

    async login(email: string, password: string): Promise<ApiResponse<any>> {
        try {
            const authData = await pb.collection("users").authWithPassword(email, password);
            return { data: authData };
        } catch (error: any) {
            console.error("Login error:", error?.status, error?.data, error?.message);
            return { error: error?.message || "Erreur lors de la connexion" };
        }
    }

    async logout(): Promise<void> {
        pb.authStore.clear();
    }

    async getCurrentUser(): Promise<ApiResponse<{ user: UserRecord }>> {
        const authErr = assertAuth();
        if (authErr) return authErr;

        try {
            const id = pb.authStore.model!.id;
            const user = await pb.collection("users").getOne<UserRecord>(id, {
                fields: "id,email,name,username,avatar,created,updated",
            });
            return { data: { user } };
        } catch (error: any) {
            console.error("Get current user error:", error?.status, error?.data, error?.message);
            return { error: error?.message || "Erreur récupération user" };
        }
    }

    // ===== USERS =====
    async searchUsers(q: string): Promise<ApiResponse<{ id: string; name?: string; email?: string }[]>> {
        try {
            const safe = q
                .replace(/\\/g, "\\\\")
                .replace(/"/g, '\\"')
                .replace(/\r?\n|\r/g, " ")
                .trim();

            if (!safe) return { data: [] };

            const res = await pb.collection("users").getList<any>(1, 10, {
                filter: `(name ~ "${safe}" || email ~ "${safe}")`,
                fields: "id,name,email",
                sort: "name",
            });

            return { data: (res.items ?? []).map((u: any) => ({ id: u.id, name: u.name, email: u.email })) };
        } catch (error: any) {
            console.error("PB searchUsers error", error?.status, error?.data, error?.message);
            return { error: error?.message || "Erreur recherche users" };
        }
    }

    async getUsersByIds(ids: string[]): Promise<ApiResponse<{ id: string; name?: string; username?: string }[]>> {
        try {
            if (!ids.length) return { data: [] };

            const or = ids.map((id) => `id = "${id}"`).join(" || ");
            const users = await pb.collection("users").getFullList<any>({
                filter: `(${or})`,
                fields: "id,name,username",
                sort: "name",
                $autoCancel: false,
            });

            return { data: users.map((u: any) => ({ id: u.id, name: u.name, username: u.username })) };
        } catch (error: any) {
            console.error("PB getUsersByIds error", error?.status, error?.data, error?.message);
            return { error: error?.message || "Erreur chargement collaborateurs" };
        }
    }

    // ===== PLANS =====
    async getPlanRecord(cloudId: string): Promise<ApiResponse<PlanRecord>> {
        try {
            const client = isAuthenticated() ? pb : pbPublic;
            const rec = await client.collection("plans").getOne<PlanRecord>(cloudId, {
                fields: "id,userId,userName,planData,isPublic,isReadOnly,collaborators,updated,created,lastEditedByName,lastEditedAt",
                $autoCancel: false,
            });
            return { data: rec };
        } catch (error: any) {
            if (error?.status === 404) return { error: "__DELETED__" };
            return { error: error?.message || "Erreur getPlanRecord" };
        }
    }

    async getPlanMeta(cloudId: string): Promise<ApiResponse<{ updated: string; lastEditedByName?: string; lastEditedAt?: string }>> {
        try {
            const client = isAuthenticated() ? pb : pbPublic;
            const rec = await client.collection("plans").getOne<any>(cloudId, {
                fields: "updated,lastEditedByName,lastEditedAt",
                $autoCancel: false,
            });
            return { data: { updated: rec.updated, lastEditedByName: rec.lastEditedByName, lastEditedAt: rec.lastEditedAt } };
        } catch (error: any) {
            if (error?.status === 404) return { error: "__DELETED__" };
            return { error: error?.message || "Erreur meta plan" };
        }
    }

    // ===== IMAGES =====
    private async getImageDisplayUrl(img: ImageRecord, isPublic: boolean): Promise<string> {
        if (isPublic) return buildFileUrl(img, img.image);

        // privé => token
        const authErr = assertAuth();
        if (authErr) return buildFileUrl(img, img.image);

        const token =
            (pb.files as any).getToken ? await (pb.files as any).getToken() : null;

        return token ? buildFileUrl(img, img.image, token) : buildFileUrl(img, img.image);
    }

    async getLatestPlanImageUrl(planId: string, isPublic: boolean): Promise<string | null> {
        try {
            const client = isPublic ? pbPublic : pb;

            const list = await client.collection("images").getList<ImageRecord>(1, 1, {
                filter: `plan = "${planId}"`,
                sort: "-created",
                fields: "id,image,created,collectionId,collectionName",
                $autoCancel: false,
            });

            const img = list.items?.[0];
            if (!img?.image) return null;

            return await this.getImageDisplayUrl(img, isPublic);
        } catch (e) {
            return null;
        }
    }

    async uploadImage(imageData: string, planId: string): Promise<ApiResponse<{ urlPublic: string }>> {
        const authErr = assertAuth();
        if (authErr) return authErr;
        if (!planId || !String(planId).trim()) return { error: "uploadImage: planId manquant" };

        try {
            const user = pb.authStore.model as any;

            const base64Data = imageData.split(",")[1];
            const byteCharacters = atob(base64Data);
            const byteNumbers = new Uint8Array(byteCharacters.length);
            for (let i = 0; i < byteCharacters.length; i++) byteNumbers[i] = byteCharacters.charCodeAt(i);

            const blob = new Blob([byteNumbers], { type: "image/png" });

            const fd = new FormData();
            fd.append("image", blob, `${planId}-${Date.now()}.png`);
            fd.append("userId", user.id);
            fd.append("plan", planId); // ✅ relation obligatoire

            const rec = await pb.collection("images").create<ImageRecord>(fd, {
                fields: "id,image,created,collectionId,collectionName",
                $autoCancel: false,
            });

            return { data: { urlPublic: buildFileUrl(rec, rec.image) } };
        } catch (error: any) {
            console.error("uploadImage error:", error?.status, error?.message, error?.data);
            return { error: error?.message || "Erreur upload image" };
        }
    }

    // ===== OPEN PLAN WITH IMAGE (source unique images) =====
    async getPlanWithImage(cloudId: string): Promise<ApiResponse<{ plan: SkiPlan; record: PlanRecord }>> {
        try {
            const client = isAuthenticated() ? pb : pbPublic;

            const rec = await client.collection("plans").getOne<PlanRecord>(cloudId, {
                fields: "id,userId,userName,planData,isPublic,isReadOnly,collaborators,updated,created,lastEditedByName,lastEditedAt",
                $autoCancel: false,
            });

            const parsed = safeJsonParse<SkiPlan>(rec.planData, {} as any);
            const imgUrl = await this.getLatestPlanImageUrl(rec.id, !!rec.isPublic);

            const plan: SkiPlan = {
                ...parsed,
                cloudId: rec.id,
                userId: rec.userId,
                userName: rec.userName,
                isPublic: rec.isPublic,
                isReadOnly: rec.isReadOnly,
                collaborators: rec.collaborators ?? [],
                siteImage: imgUrl ?? null,
                updatedAt: rec.updated,
                createdAt: rec.created,
            } as any;

            return { data: { plan, record: rec } };
        } catch (error: any) {
            console.error("getPlanWithImage error:", error?.status, error?.message, error?.data);
            if (error?.status === 404) return { error: "__DELETED__" };
            return { error: error?.message || "Erreur getPlanWithImage" };
        }
    }

    async getPublicPlans(): Promise<ApiResponse<{ plans: SkiPlan[] }>> {
        try {
            const records = await pbPublic.collection("plans").getFullList<PlanRecord>({
                fields: "id,planData,isPublic,isReadOnly,updated,created,userId,userName,collaborators",
                filter: "isPublic = true",
                sort: "-created",
                $autoCancel: false,
            });

            const plans = records.map((rec) => {
                const p = safeJsonParse<SkiPlan>(rec.planData, {} as any);
                return {
                    ...p,
                    cloudId: rec.id,
                    userId: rec.userId,
                    userName: rec.userName,
                    isPublic: rec.isPublic,
                    isReadOnly: rec.isReadOnly,
                    collaborators: rec.collaborators ?? [],
                    // Liste “light”: image optionnelle, tu peux laisser null
                    siteImage: null,
                    updatedAt: rec.updated,
                    createdAt: rec.created,
                } as any;
            });

            return { data: { plans } };
        } catch (error: any) {
            console.error("getPublicPlans error:", error?.status, error?.data, error?.message);
            return { error: error?.message || "Erreur chargement plans publics" };
        }
    }

    async getMyPlans(): Promise<ApiResponse<{ plans: SkiPlan[] }>> {
        const authErr = assertAuth();
        if (authErr) return authErr;

        try {
            const user = pb.authStore.model as any;

            const records = await pb.collection("plans").getFullList<PlanRecord>({
                fields: "id,planData,isPublic,isReadOnly,updated,created,userId,userName,collaborators",
                filter: `userId = "${user.id}"`,
                sort: "-created",
                $autoCancel: false,
            });

            const plans = records.map((rec) => {
                const p = safeJsonParse<SkiPlan>(rec.planData, {} as any);
                return {
                    ...p,
                    cloudId: rec.id,
                    userId: rec.userId,
                    userName: rec.userName,
                    isPublic: rec.isPublic,
                    isReadOnly: rec.isReadOnly,
                    collaborators: rec.collaborators ?? [],
                    siteImage: null,
                    updatedAt: rec.updated,
                    createdAt: rec.created,
                } as any;
            });

            return { data: { plans } };
        } catch (error: any) {
            console.error("getMyPlans error:", error?.status, error?.data, error?.message);
            return { error: error?.message || "Erreur chargement mes plans" };
        }
    }

    // ===== SAVE PLAN (light) =====
    async savePlanToCloud(args: { cloudId?: string; plan: SkiPlan; isPublic: boolean; isReadOnly: boolean }): Promise<ApiResponse<{ cloudId: string; updated: string }>> {
        const authErr = assertAuth();
        if (authErr) return authErr;

        try {
            const user = pb.authStore.model as any;

            const planLight: SkiPlan = { ...args.plan, cloudId: args.cloudId, siteImage: null };

            const payload: any = {
                userId: user.id,
                userName: user.name || user.username || user.email,
                planData: JSON.stringify(planLight),
                isPublic: args.isPublic,
                isReadOnly: args.isReadOnly,
                lastEditedById: user.id,
                lastEditedByName: user.name || user.username || user.email,
                lastEditedAt: pbFilterDate(new Date()),
            };

            let cloudId = args.cloudId;

            if (!cloudId) {
                const created = await pb.collection("plans").create<any>(payload, { fields: "id,updated" });
                return { data: { cloudId: created.id, updated: created.updated } };
            }

            const existing = await pb.collection("plans").getOne<PlanRecord>(cloudId, { fields: "userId,collaborators" });
            if (!canEditRecord(existing, user.id)) return { error: "Non autorisé" };

            const updated = await pb.collection("plans").update<any>(cloudId, payload, { fields: "id,updated" });
            return { data: { cloudId, updated: updated.updated } };
        } catch (e: any) {
            console.error("savePlanToCloud error:", e?.status, e?.message, e?.data);
            return { error: e?.message || "Erreur save cloud" };
        }
    }

    // ===== PRESENCE (plan_sessions.planId) =====
    async upsertPlanSession(planId: string, mode: "view" | "edit", clientId: string) {
        const authErr = assertAuth();
        if (authErr) return authErr;

        try {
            const user = pb.authStore.model as any;

            const filter = `planId = "${planId}" && userId = "${user.id}" && clientId = "${clientId}"`;

            const payload: any = {
                planId,
                userId: user.id,
                userName: user.name || user.username || user.email,
                mode,
                clientId,
                lastSeen: pbFilterDate(new Date()),
            };

            try {
                const existing = await pb.collection("plan_sessions").getFirstListItem<any>(filter, { $autoCancel: false });
                const rec = await pb.collection("plan_sessions").update<any>(existing.id, payload, { $autoCancel: false });
                return { data: rec };
            } catch {
                const rec = await pb.collection("plan_sessions").create<any>(payload, { $autoCancel: false });
                return { data: rec };
            }
        } catch (err: any) {
            console.error("upsertPlanSession error:", err?.status, err?.message, err?.data);
            return { error: err?.message || "Failed" };
        }
    }

    async listActiveSessions(planId: string, activeWithinSeconds = 60) {
        try {
            const since = pbFilterDate(new Date(Date.now() - activeWithinSeconds * 1000));
            const records = await pb.collection("plan_sessions").getFullList<any>({
                filter: `planId = "${planId}" && lastSeen >= "${since}"`,
                sort: "-lastSeen",
                $autoCancel: false,
            });
            return { data: records };
        } catch (err: any) {
            console.error("listActiveSessions error:", err?.status, err?.message, err?.data);
            return { error: err?.message || "Failed" };
        }
    }

    async deletePlanSession(sessionId: string) {
        try {
            await pb.collection("plan_sessions").delete(sessionId);
            return { data: true };
        } catch (err: any) {
            console.error("deletePlanSession error:", err?.status, err?.data, err?.message);
            return { error: err?.message || "Failed" };
        }
    }

    async addCollaborator(cloudId: string, userIdToAdd: string): Promise<ApiResponse<{ updated: string; collaborators: string[] }>> {
        const authErr = assertAuth();
        if (authErr) return authErr;

        try {
            const user = pb.authStore.model as any;
            const rec = await pb.collection("plans").getOne<PlanRecord>(cloudId, { fields: "userId,collaborators,updated" });
            if (rec.userId !== user.id) return { error: "Seul le propriétaire peut gérer les collaborateurs" };

            const current = rec.collaborators ?? [];
            const next = Array.from(new Set([...current, userIdToAdd]));

            const updatedRec = await pb.collection("plans").update<any>(cloudId, { collaborators: next }, { fields: "updated,collaborators" });
            return { data: { updated: updatedRec.updated, collaborators: updatedRec.collaborators ?? [] } };
        } catch (error: any) {
            return { error: error?.message || "Erreur ajout collaborateur" };
        }
    }

    async removeCollaborator(cloudId: string, userIdToRemove: string): Promise<ApiResponse<{ updated: string; collaborators: string[] }>> {
        const authErr = assertAuth();
        if (authErr) return authErr;

        try {
            const user = pb.authStore.model as any;
            const rec = await pb.collection("plans").getOne<PlanRecord>(cloudId, { fields: "userId,collaborators,updated" });
            if (rec.userId !== user.id) return { error: "Seul le propriétaire peut gérer les collaborateurs" };

            const current = rec.collaborators ?? [];
            const next = current.filter((id) => id !== userIdToRemove);

            const updatedRec = await pb.collection("plans").update<any>(cloudId, { collaborators: next }, { fields: "updated,collaborators" });
            return { data: { updated: updatedRec.updated, collaborators: updatedRec.collaborators ?? [] } };
        } catch (error: any) {
            return { error: error?.message || "Erreur suppression collaborateur" };
        }
    }
}

const service = new PocketBaseService();
export default service;

// Exports "faciles"
export const signup = service.signup.bind(service);
export const login = service.login.bind(service);
export const logout = service.logout.bind(service);
export const getCurrentUser = service.getCurrentUser.bind(service);

export const searchUsers = service.searchUsers.bind(service);
export const getUsersByIds = service.getUsersByIds.bind(service);

export const getPlanMeta = service.getPlanMeta.bind(service);
export const getPlanRecord = service.getPlanRecord.bind(service);
export const getPlanWithImage = service.getPlanWithImage.bind(service);

export const getPublicPlans = service.getPublicPlans.bind(service);
export const getMyPlans = service.getMyPlans.bind(service);

export const savePlanToCloud = service.savePlanToCloud.bind(service);
export const uploadImage = service.uploadImage.bind(service);

export const upsertPlanSession = service.upsertPlanSession.bind(service);
export const listActiveSessions = service.listActiveSessions.bind(service);
export const deletePlanSession = service.deletePlanSession.bind(service);

export const addCollaborator = service.addCollaborator.bind(service);
export const removeCollaborator = service.removeCollaborator.bind(service);